/**
 * ContactPage — IMPERIA AI
 *
 * Hybride aanpak (door gebruiker gekozen):
 *  1. Hero met duidelijke positionering
 *  2. Intent-routing: 3 paden (Gratis scan · Self-service · Managed strategiegesprek)
 *  3. Contact-info kolom + universeel contactformulier
 *  4. WhatsApp / telefoon snelle routes
 *  5. FAQ specifiek voor contact-fase
 *  6. Closing CTA
 *
 * SEO: H1 + H2 hiërarchie, schema.org ContactPage + LocalBusiness + FAQPage.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { contactInfo } from '../data/mock';
import {
  Mail, Phone, MapPin, ArrowRight, Send, CheckCircle2, Clock,
  Sparkles, Zap, Crown, Calendar, ShieldCheck,
  ChevronRight, Bot, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Intent-routes ─── */
const INTENT_ROUTES = [
  {
    id: 'gratis-scan',
    icon: Search,
    accent: '#64748b',
    badge: 'Gratis · 60 sec',
    title: 'Ik wil eerst zien hoe Google mijn site ziet',
    desc: 'Start met de gratis online groeiscan. Geen creditcard, geen verplichting — direct uw score + top-10 verbeterpunten + 3 backlink-kansen.',
    cta: { label: 'Start gratis scan', href: '/register' },
  },
  {
    id: 'self-service',
    icon: Zap,
    accent: '#2563eb',
    badge: '€14,95 p/m',
    title: 'Ik wil zelf uitvoeren met AI-suggesties',
    desc: 'Connected Self-Service: live dashboard, AI-suggesties per opdracht en stap-voor-stap guides per CMS. Maandelijks opzegbaar.',
    cta: { label: 'Bekijk Self-Service', href: '/prijzen#self' },
  },
  {
    id: 'managed',
    icon: Crown,
    accent: '#D4A017',
    badge: 'Strategiegesprek',
    title: 'Ik wil dat AI het werk voor mij doet',
    desc: 'Managed AI Bot — wij plannen een 30-min strategiegesprek met onze growth-architect. Geen pitch, wel een eerlijk plan.',
    cta: { label: 'Plan strategiegesprek', href: '#contact-form' },
  },
];

/* ─── Quick contact methods ─── */
const QUICK_METHODS = [
  {
    icon: Mail,
    label: 'E-mail',
    value: contactInfo.email,
    href: `mailto:${contactInfo.email}`,
    note: 'Antwoord binnen 24 uur (vaak < 2u op werkdagen)',
  },
  {
    icon: Phone,
    label: 'Telefoon',
    value: contactInfo.phone,
    href: `tel:${contactInfo.phone.replace(/\s/g, '')}`,
    note: 'Ma-vr 09:00 – 17:30',
  },
  {
    icon: MapPin,
    label: 'Bezoek',
    value: contactInfo.address,
    href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactInfo.address)}`,
    note: 'Op afspraak — koffie staat klaar',
  },
];

/* ─── FAQ specifiek voor contact-fase ─── */
const CONTACT_FAQ = [
  {
    q: 'Hoe snel krijg ik een reactie?',
    a: 'Binnen 24 uur — meestal binnen 2 uur op werkdagen. Voor strategiegesprekken plannen wij vaak nog dezelfde week.',
  },
  {
    q: 'Moet ik direct kiezen tussen Self-Service en Managed AI?',
    a: 'Nee. Veel klanten starten met de gratis scan, koppelen daarna hun kanalen (gratis) en upgraden pas als de resultaten zichtbaar zijn. Geen druk.',
  },
  {
    q: 'Werken jullie ook met klanten buiten Zwolle?',
    a: 'Ja. Wij werken landelijk in Nederland en België. Strategiegesprekken zijn standaard online; on-site bezoeken op aanvraag.',
  },
  {
    q: 'Kan ik mijn huidige bureau gewoon naast IMPERIA AI houden?',
    a: 'Absoluut. Veel klanten gebruiken ons in eerste instantie als second-opinion of voor specifieke domeinen (bijv. alleen backlinks of GBP). U bepaalt wat u uit handen geeft.',
  },
];

/* ─── Schema.org ─── */
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact — IMPERIA AI",
    "url": "https://www.imperiaai.nl/contact",
    "description": "Neem contact op met IMPERIA AI voor een gratis scan, Self-Service of Managed AI strategiegesprek.",
  },
  {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "IMPERIA AI",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Paxtonstraat 3n",
      "postalCode": "8013 RP",
      "addressLocality": "Zwolle",
      "addressCountry": "NL",
    },
    "telephone": contactInfo.phone,
    "email": contactInfo.email,
    "openingHours": "Mo-Fr 09:00-17:30",
    "url": "https://www.imperiaai.nl",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": CONTACT_FAQ.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    type: 'algemeen',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/form-submit`, form);
      setSent(true);
      toast.success('Bericht verzonden — wij reageren binnen 24 uur');
    } catch {
      toast.error('Er ging iets mis. Probeer opnieuw of mail direct.');
    } finally {
      setSending(false);
    }
  };

  const scrollToForm = (e) => {
    e.preventDefault();
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title="Contact · Uw AI Marketing Manager voor MKB — IMPERIA AI Zwolle"
        description="Contact met IMPERIA AI — uw AI Marketing Manager voor MKB & zzp. Gratis online groeiscan, vragen over Connected Self-Service (€14,95 p/m) of een strategiegesprek over Managed AI. Reactie binnen 24 uur."
        canonical="/contact"
        keywords="contact imperia ai zwolle, ai marketing manager mkb contact, online marketing strategiegesprek, lokale seo nederland contact, ai seo gesprek mkb"
        schema={SCHEMA}
      />

      {/* ─── Hero ───────────────────────────────────────────── */}
      <section
        style={{
          background:
            'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
          padding: '120px 0 70px',
        }}
        data-testid="contact-hero"
      >
        <div className="container-custom max-w-5xl mx-auto px-4 text-center">
          <span
            style={{
              color: '#D4A017',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
            }}
          >
            Contact · Strategiegesprek · Gratis scan
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mt-5 mb-6"
            style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.05 }}
          >
            Wat past het beste{' '}
            <span style={{ color: '#D4A017' }}>bij u</span>?
          </h1>
          <p className="text-white/75 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Drie duidelijke paden: gratis scan, zelf uitvoeren met AI-suggesties,
            of AI laat het werk voor u doen. Kies de route die past — of stel ons
            eerst een vraag.
          </p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link
              to="/register"
              data-testid="hero-cta-scan"
              className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-6 py-3 rounded-xl transition-colors"
            >
              Start gratis scan <ArrowRight size={16} />
            </Link>
            <a
              href="#contact-form"
              onClick={scrollToForm}
              data-testid="hero-cta-form"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              Stel een vraag <ChevronRight size={16} />
            </a>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 justify-center text-white/60 text-xs">
            <span className="inline-flex items-center gap-1.5"><Clock size={12} /> Reactie &lt; 24u</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} /> Geen verkoop-pitch</span>
            <span className="inline-flex items-center gap-1.5"><Bot size={12} /> Eerlijk over wat AI kan en niet kan</span>
          </div>
        </div>
      </section>

      {/* ─── Intent routing (3 cards) ───────────────────────── */}
      <section className="bg-white py-16" data-testid="intent-routing">
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <p
              style={{
                fontSize: 11,
                color: '#D4A017',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              Stap 1 — Kies uw route
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#0f172a]"
              style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.15 }}
            >
              Wat brengt u hier?
            </h2>
            <p className="text-[#475569] mt-3 max-w-2xl mx-auto">
              Wij sturen u meteen door naar de juiste flow — geen rondloperij,
              geen 4 e-mails over en weer.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {INTENT_ROUTES.map((route) => (
              <div
                key={route.id}
                data-testid={`intent-card-${route.id}`}
                className="bg-[#f8fafc] border border-slate-200 hover:border-[#D4A017] rounded-2xl p-6 transition-all hover:shadow-lg flex flex-col"
              >
                <div className="flex items-center justify-between mb-4">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center"
                    style={{ background: `${route.accent}15` }}
                  >
                    <route.icon size={20} style={{ color: route.accent }} />
                  </div>
                  <span
                    className="text-xs font-bold px-2.5 py-1 rounded-full"
                    style={{
                      background: `${route.accent}15`,
                      color: route.accent,
                    }}
                  >
                    {route.badge}
                  </span>
                </div>
                <h3
                  className="text-lg font-bold text-[#0f172a] mb-2 leading-snug"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  {route.title}
                </h3>
                <p className="text-sm text-[#475569] leading-relaxed mb-5 flex-1">
                  {route.desc}
                </p>
                {route.cta.href.startsWith('#') ? (
                  <a
                    href={route.cta.href}
                    onClick={scrollToForm}
                    data-testid={`intent-cta-${route.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0f172a] hover:text-[#D4A017] transition-colors"
                  >
                    {route.cta.label} <ArrowRight size={14} />
                  </a>
                ) : (
                  <Link
                    to={route.cta.href}
                    data-testid={`intent-cta-${route.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0f172a] hover:text-[#D4A017] transition-colors"
                  >
                    {route.cta.label} <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Quick contact methods ──────────────────────────── */}
      <section className="bg-[#f8fafc] py-14" data-testid="quick-methods">
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <p
              style={{
                fontSize: 11,
                color: '#D4A017',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              Stap 2 — Of bereik ons direct
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#0f172a]"
              style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.15 }}
            >
              Direct contact
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {QUICK_METHODS.map((m) => (
              <a
                key={m.label}
                href={m.href}
                target={m.label === 'Bezoek' ? '_blank' : undefined}
                rel={m.label === 'Bezoek' ? 'noopener noreferrer' : undefined}
                data-testid={`quick-method-${m.label.toLowerCase()}`}
                className="bg-white border border-slate-200 hover:border-[#D4A017] rounded-2xl p-6 transition-all hover:shadow-md group block"
              >
                <div className="w-11 h-11 rounded-xl bg-[#D4A017]/10 flex items-center justify-center mb-3 group-hover:bg-[#D4A017]/20 transition-colors">
                  <m.icon size={18} className="text-[#D4A017]" />
                </div>
                <p className="text-[#94a3b8] text-xs uppercase tracking-wide font-semibold mb-1">
                  {m.label}
                </p>
                <p className="text-[#0f172a] font-bold text-base group-hover:text-[#D4A017] transition-colors break-words">
                  {m.value}
                </p>
                <p className="text-[#64748b] text-xs mt-2 leading-snug">
                  {m.note}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Universal form ─────────────────────────────────── */}
      <section
        id="contact-form"
        className="bg-white py-16"
        data-testid="contact-form-section"
      >
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Left column — context */}
            <div className="lg:col-span-2 lg:sticky lg:top-24">
              <p
                style={{
                  fontSize: 11,
                  color: '#D4A017',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                Stap 3 — Stuur uw vraag
              </p>
              <h2
                className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-4 leading-tight"
                style={{ fontFamily: "'Sora', sans-serif" }}
              >
                Vertel ons wat u zoekt
              </h2>
              <p className="text-[#475569] mb-6 leading-relaxed">
                Hoe specifieker uw vraag, hoe gerichter wij reageren. Geef aan
                of u een vraag heeft, een offerte wilt of een
                strategiegesprek wil inplannen.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-[#D4A017] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#0f172a]">
                    <strong>Geen verkoop-funnel:</strong> u krijgt een echt
                    persoon, geen geautomatiseerde e-mail-sequence.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-[#D4A017] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#0f172a]">
                    <strong>Eerlijk advies:</strong> als IMPERIA AI niet past,
                    zeggen wij dat — en raden waar mogelijk een alternatief aan.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-[#D4A017] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#0f172a]">
                    <strong>Reactietijd:</strong> binnen 24 uur, vaak binnen 2
                    uur op werkdagen.
                  </p>
                </div>
              </div>
              <div className="mt-7 bg-[#0f172a] rounded-2xl p-5 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar size={16} className="text-[#D4A017]" />
                  <p className="text-xs uppercase tracking-wide text-white/60 font-semibold">
                    Liever direct plannen?
                  </p>
                </div>
                <p className="text-sm text-white/85 leading-relaxed">
                  Bel{' '}
                  <a
                    href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                    className="font-bold text-[#D4A017] hover:underline"
                  >
                    {contactInfo.phone}
                  </a>{' '}
                  of mail naar{' '}
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="font-bold text-[#D4A017] hover:underline"
                  >
                    {contactInfo.email}
                  </a>
                  .
                </p>
              </div>
            </div>

            {/* Right column — form */}
            <div className="lg:col-span-3">
              {sent ? (
                <div
                  data-testid="contact-success"
                  className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-10 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 size={32} className="text-emerald-600" />
                  </div>
                  <h2
                    className="text-2xl font-bold text-[#0f172a] mb-2"
                    style={{ fontFamily: "'Sora', sans-serif" }}
                  >
                    Bericht verzonden
                  </h2>
                  <p className="text-[#475569] mb-6">
                    Bedankt voor uw bericht. Wij reageren binnen 24 uur — vaak
                    sneller.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Link
                      to="/register"
                      className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-6 py-3 rounded-xl"
                    >
                      Start gratis scan <ArrowRight size={16} />
                    </Link>
                    <Link
                      to="/diensten"
                      className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#D4A017] text-[#0f172a] font-semibold px-6 py-3 rounded-xl"
                    >
                      Bekijk diensten
                    </Link>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="contact-form"
                  className="bg-[#f8fafc] border border-slate-200 rounded-2xl p-6 md:p-8 space-y-4"
                >
                  {/* Intent selector */}
                  <div>
                    <label className="text-xs uppercase tracking-wide text-[#64748b] font-semibold mb-2 block">
                      Waar gaat het over?
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {[
                        { v: 'algemeen', l: 'Algemeen' },
                        { v: 'self-service', l: 'Self-Service' },
                        { v: 'managed', l: 'Managed AI' },
                        { v: 'partner', l: 'Partnership' },
                      ].map((opt) => (
                        <button
                          key={opt.v}
                          type="button"
                          data-testid={`form-type-${opt.v}`}
                          onClick={() => setForm({ ...form, type: opt.v })}
                          className={`text-xs font-semibold px-3 py-2.5 rounded-xl border transition-all ${
                            form.type === opt.v
                              ? 'bg-[#D4A017] border-[#D4A017] text-[#0f172a]'
                              : 'bg-white border-slate-300 text-[#475569] hover:border-[#D4A017]'
                          }`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-wide text-[#64748b] font-semibold mb-1.5 block">
                        Naam *
                      </label>
                      <input
                        data-testid="contact-name"
                        required
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                        placeholder="Uw naam"
                        className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/20 transition-all"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wide text-[#64748b] font-semibold mb-1.5 block">
                        E-mail *
                      </label>
                      <input
                        data-testid="contact-email"
                        required
                        type="email"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                        placeholder="naam@bedrijf.nl"
                        className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/20 transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-wide text-[#64748b] font-semibold mb-1.5 block">
                        Telefoon (optioneel)
                      </label>
                      <input
                        data-testid="contact-phone"
                        value={form.phone}
                        onChange={(e) =>
                          setForm({ ...form, phone: e.target.value })
                        }
                        placeholder="06 - 12345678"
                        className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/20 transition-all"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wide text-[#64748b] font-semibold mb-1.5 block">
                        Bedrijf (optioneel)
                      </label>
                      <input
                        data-testid="contact-company"
                        value={form.company}
                        onChange={(e) =>
                          setForm({ ...form, company: e.target.value })
                        }
                        placeholder="Bedrijfsnaam"
                        className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/20 transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wide text-[#64748b] font-semibold mb-1.5 block">
                      Bericht *
                    </label>
                    <textarea
                      data-testid="contact-message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                      }
                      placeholder="Vertel kort wat u zoekt: welke dienst, welk doel, en welke vraag wij voor u kunnen beantwoorden…"
                      className="w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-[#0f172a] focus:border-[#D4A017] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/20 transition-all resize-none"
                    />
                  </div>

                  <button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={sending}
                    className="w-full bg-[#D4A017] hover:bg-[#B8860B] disabled:opacity-50 disabled:cursor-not-allowed text-[#0f172a] font-bold px-6 py-4 rounded-xl flex items-center justify-center gap-2 transition-colors"
                  >
                    {sending ? (
                      'Versturen…'
                    ) : (
                      <>
                        Verstuur bericht <Send size={16} />
                      </>
                    )}
                  </button>
                  <p className="text-xs text-[#94a3b8] text-center">
                    Door te versturen gaat u akkoord met onze{' '}
                    <Link
                      to="/privacy"
                      className="text-[#D4A017] underline hover:no-underline"
                    >
                      privacyverklaring
                    </Link>
                    .
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ────────────────────────────────────────────── */}
      <section className="bg-[#f8fafc] py-16" data-testid="contact-faq">
        <div className="container-custom max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <p
              style={{
                fontSize: 11,
                color: '#D4A017',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              Veelgestelde vragen
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#0f172a]"
              style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.15 }}
            >
              Voordat u contact opneemt
            </h2>
          </div>
          <div className="space-y-3">
            {CONTACT_FAQ.map((f, i) => (
              <details
                key={i}
                data-testid={`faq-item-${i}`}
                className="bg-white border border-slate-200 hover:border-[#D4A017] rounded-2xl p-5 group transition-colors"
              >
                <summary className="cursor-pointer font-bold text-[#0f172a] flex items-center justify-between list-none">
                  <span>{f.q}</span>
                  <ChevronRight
                    size={18}
                    className="text-[#D4A017] transition-transform group-open:rotate-90"
                  />
                </summary>
                <p className="text-sm text-[#475569] mt-3 leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Closing CTA ────────────────────────────────────── */}
      <section
        style={{
          background:
            'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
          padding: '70px 0',
        }}
        data-testid="contact-closing-cta"
      >
        <div className="container-custom max-w-4xl mx-auto px-4 text-center">
          <Sparkles size={32} className="text-[#D4A017] mx-auto mb-4" />
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: "'Sora', sans-serif", lineHeight: 1.15 }}
          >
            Eerst voelen wat het oplevert?
          </h2>
          <p className="text-white/70 mb-7 max-w-2xl mx-auto leading-relaxed">
            Start vrijblijvend met de gratis online groeiscan — binnen 60
            seconden ziet u uw score, top-10 verbeterpunten en 3
            backlink-kansen voor uw branche.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/register"
              data-testid="closing-cta-scan"
              className="inline-flex items-center gap-2 bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-7 py-3.5 rounded-xl transition-colors"
            >
              Start gratis scan <ArrowRight size={16} />
            </Link>
            <Link
              to="/prijzen"
              data-testid="closing-cta-pricing"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/20 text-white font-semibold px-7 py-3.5 rounded-xl transition-colors"
            >
              Bekijk prijzen <ChevronRight size={16} />
            </Link>
          </div>
          <p className="text-white/40 text-xs mt-6">
            Geen creditcard nodig · Maandelijks opzegbaar · Geen lock-in
          </p>
        </div>
      </section>
    </>
  );
}
